import HtmlParseStringify from 'html-parse-stringify';
import unescapeHtml from 'unescape-html';

import {
  MarkupBBCodeVariant,
  MarkupFlavour,
  MarkupLatexType,
  MarkupMarkdownVariant,
  MarkupNodeType,
  MarkupUnorderedListType,
  MARKUP_NODE_TYPE_ROOT,
  SUPPORTED_CODE_LANGS
} from '#src/constants.js';
import elementsCode from '#src/elements/code.js';
import elementsEscaped from '#src/elements/escaped.js';
import elementsLinks from '#src/elements/links.js';
import elementsLists from '#src/elements/lists.js';
import elementsMisc from '#src/elements/misc.js';
import elementsShitty from '#src/elements/shitty.js';
import elementsTags from '#src/elements/tags.js';
import {fromBase64} from '#src/helper.js';
import {getListBullet} from '#src/lists.js';
import ProcessingContext, {SkipType} from '#src/processing-context.js';

const ELEMENTS = [
  ...elementsCode,
  ...elementsEscaped,
  ...elementsLinks,
  ...elementsLists,
  ...elementsMisc,
  ...elementsShitty,
  ...elementsTags
].sort((i1, i2) => i1.priority - i2.priority);

const MARKDOWN_VARIANT_MAP = new Map([
  [MarkupMarkdownVariant.Asterisk, ['*']],
  [MarkupMarkdownVariant.Slash, ['/', 3]],
  [MarkupMarkdownVariant.Underscore, ['_']]
]);

const NODE_TYPES_NO_CONTENT = new Set([
  MarkupNodeType.Code,
  MarkupNodeType.Command,
  MarkupNodeType.Latex,
  MarkupNodeType.LineBreak,
  MarkupNodeType.Link,
  MarkupNodeType.Monospace,
  MarkupNodeType.NoMarkup,
  MarkupNodeType.PostLink,
  MarkupNodeType.Preformatted,
  MarkupNodeType.Text
]);

const NODE_TYPES_TEXT_CONTENT = new Set([
  MarkupNodeType.Code,
  MarkupNodeType.Latex,
  MarkupNodeType.Link,
  MarkupNodeType.Monospace,
  MarkupNodeType.NoMarkup,
  MarkupNodeType.Preformatted
]);

function _getIndE({context, rxOp, matchs, rxCl, inds, nestable, escapable, nested}) {
  nested.nested = false;

  if (!nestable) {
    return (inds >= 0) ? context.find(rxCl, inds + matchs[0].length, escapable) : -1;
  }

  if (inds >= 0) {
    let matchst = context.find(rxOp, inds + matchs[0].length, escapable);
    let matchet = context.find(rxCl, inds + matchs[0].length, escapable);

    let depth = 1;

    while (matchst || matchet) {
      const isStart = matchst && (!matchet || (matchst.index < matchet.index));

      const tmp = isStart ? matchst : matchet;

      const offs = isStart ? matchst[0].length : matchet[0].length;

      depth += (tmp.index === (matchst ? matchst.index : -1)) ? 1 : -1;

      if (depth > 1) {
        nested.nested = true;
      }

      if (!depth) {
        return tmp;
      }

      matchst = context.find(rxOp, tmp.index + offs, escapable);
      matchet = context.find(rxCl, tmp.index + offs, escapable);
    }
  }

  return null;
}

function _mapDataKey(key) {
  if (key === 'data-data-board-name') {
    return 'boardName';
  }

  if (key === 'data-data-post-number') {
    return 'postNumber';
  }

  return key.replace(/^data-data-/u, '');
}

function _mapDataValue(nodeType, key, value) {
  switch (nodeType) {
  case MarkupNodeType.Command:
    return (key === 'data-data-source') ? fromBase64(value) : value;
  case MarkupNodeType.Code:
  case MarkupNodeType.Latex:
  case MarkupNodeType.Monospace:
  case MarkupNodeType.NoMarkup:
  case MarkupNodeType.Preformatted:
    return (key === 'data-data-text') ? fromBase64(value) : value;
  case MarkupNodeType.CollapsibleSpoiler:
    return (key === 'data-data-title') ? fromBase64(value) : value;
  case MarkupNodeType.ListItem:
    return (key === 'data-data-value') ? Number(value) : value;
  case MarkupNodeType.Tooltip:
    return (key === 'data-data-tooltip') ? fromBase64(value) : value;
  case MarkupNodeType.Link:
    return (key === 'data-data-text') ? fromBase64(value) : value;
  case MarkupNodeType.PostLink:
    return (key === 'data-data-post-number') ? Number(value) : value;
  default:
    break;
  }

  return value;
}

function _process({check, context, conversionFunction, escapable, nestable, rxOp, rxCl = rxOp}) {
  const nested = {nested: false};

  let matchs = context.find(rxOp, 0, escapable);

  const getMatchE = () => (rxCl ? _getIndE({
    context,
    escapable,
    matchs,
    nestable,
    nested,
    rxCl,
    rxOp,
    inds: (matchs ? matchs.index : -1)
  }) : null);

  let matche = getMatchE();

  let rerun = false;

  const f = () => {
    if (!matchs || (rxCl && (!matche || (matche.index <= matchs.index)))) {
      return;
    }

    if ((typeof check === 'function') && !check(context, matchs, matche)) {
      if (rxCl && matche) {
        matchs = context.find(rxOp, matche.index + matche[0].length, escapable);
      } else {
        matchs = context.find(rxOp, matchs.index + matchs[0].length, escapable);
      }

      matche = getMatchE();

      return f();
    }

    const options = {
      cl: '',
      op: '',
      skipType: SkipType.NoSkip
    };

    const start = matche ? (matchs.index + matchs[0].length) : matchs.index;
    const end = matche ? (matche.index - matchs.index - matchs[0].length) : (matchs.index + matchs[0].length);

    const text = conversionFunction({
      context,
      options,
      matchEnd: matche,
      matchStart: matchs,
      text: context.text.substr(start, end)
    });

    if (text) {
      if (options.cl) {
        context.insert(rxCl ? (matche.index + matche[0].length) : (matchs.index + matchs[0].length), options.cl);
      }

      if (rxCl) {
        context.replace({
          text,
          correction: matchs[0].length,
          length: matche.index - matchs.index + matche[0].length,
          skipType: options.skipType,
          start: matchs.index
        });
      } else {
        context.replace({
          text,
          correction: matchs[0].length,
          length: matchs[0].length,
          skipType: options.skipType,
          start: matchs.index
        });
      }

      if (options.op) {
        context.insert(matchs.index, options.op);
      }

      matchs = context.find(rxOp, matchs.index + text.length + options.op.length + options.cl.length, escapable);
    } else if (rxCl) {
      matchs = context.find(
        rxOp,
        matche ? (matche.index + matche[0].length) : (matchs.index + matchs[0].length),
        escapable
      );
    } else {
      matchs = context.find(rxOp, matchs.index + matchs[0].index, escapable);
    }

    if (nestable && nested.nested) {
      rerun = true;
    }

    matche = getMatchE();

    return f();
  };

  f();

  if (rerun) {
    return _process({check, context, conversionFunction, escapable, nestable, rxCl, rxOp});
  }
}

function _codeToText({content, data, meta, pure = false}) {
  if (pure) {
    return content;
  }

  if (meta.flavour === MarkupFlavour.Markdown) {
    return `/--code ${data.lang}\n${content}\n\\--`;
  }

  if (meta.variant === MarkupBBCodeVariant.Short) {
    return `[${data.lang}]${content}[/${data.lang}]`;
  }

  return `[code${data?.lang ? ` lang="${data.lang}"` : ''}]${content}[/code]`;
}

function _latexToText({content, data, pure = false}) {
  if (pure) {
    return content;
  }

  const t = (data.type === 'inline') ? 'l' : 'latex';

  return `[${t}]${content}[/${t}]`;
}

function _linkToText({content, meta, pure = false}) {
  if (pure) {
    return content;
  }

  if (meta.flavour === MarkupFlavour.Markdown) {
    return content;
  }

  return `[url]${content}[/url]`;
}

const _nodeToText = (boardName, pure = false, options = {}, list = {}) => ({content, data, meta, type}, index) => {
  switch (type) {
  case MarkupNodeType.Bold:
    return _tagToText(boardName, 'b', 2, {content, meta, options, pure});
  case MarkupNodeType.Code:
    return _codeToText({content, data, meta, pure});
  case MarkupNodeType.CollapsibleSpoiler: {
    const c = content.map(_nodeToText(boardName, pure, options)).join('');

    if (pure) {
      return c;
    }

    return `[cspoiler${data?.title ? ` title="${data.title}"` : ''}]${c}[/cspoiler]`;
  }

  case MarkupNodeType.Command: {
    if (options.commandOutput) {
      return content ? content.map(_nodeToText(boardName, pure, options)).join('') : '';
    }

    if (pure) {
      return data.source;
    }

    const [op, cl] = (meta.flavour === MarkupFlavour.BBCode) ? ['[command]', '[/command]'] : ['[[', ']]'];

    return `${op}${data.source}${cl}`;
  }

  case MarkupNodeType.Italics:
    return _tagToText(boardName, 'i', 1, {content, meta, options, pure});
  case MarkupNodeType.Latex:
    return _latexToText({content, data, pure});
  case MarkupNodeType.LineBreak:
    return '\n'.repeat(data.count);
  case MarkupNodeType.Link:
    return _linkToText({content, meta, pure});
  case MarkupNodeType.ListItem: {
    const c = content.map(_nodeToText(boardName, pure, options)).join('');

    if (!pure) {
      return `[li${data?.value ? ` value="${data.value}"` : ''}]${c}[/li]\n`;
    }

    if (!options.symbols) {
      return c;
    }

    return `${getListBullet(list.type, list.data, Math.max(data?.value || (index + 1), 1))} ${c}`;
  }

  case MarkupNodeType.Monospace:
    return _tagToText(boardName, 'm', '``', {content, meta, options, pure});
  case MarkupNodeType.NoMarkup:
    return _tagToText(boardName, 'n', "''", {content, meta, options, pure});
  case MarkupNodeType.OrderedList: {
    const c = content.map(_nodeToText(boardName, pure, options, {data, type})).join('');

    if (pure) {
      return c;
    }

    return `[ol type="${data.type}"]\n${c}[/ol]`;
  }

  case MarkupNodeType.PostLink:
    return `>>${(data.boardName === boardName) ? '' : `/${data.boardName}/`}${data.postNumber}`;
  case MarkupNodeType.Preformatted:
    return _tagToText(boardName, 'pre', ['/--pre\n', '\n\\--'], {content, meta, options, pure});
  case MarkupNodeType.Quotation:
    return `>${content.map(_nodeToText(boardName, pure, options)).join('')}`;
  case MarkupNodeType.Spoiler:
    return _tagToText(boardName, 'spoiler', '%%', {content, meta, options, pure});
  case MarkupNodeType.Strikethrough:
    return _tagToText(boardName, 's', '---', {content, meta, options, pure});
  case MarkupNodeType.Subscript:
    return _tagToText(boardName, 'sub', 0, {content, meta, options, pure});
  case MarkupNodeType.Superscript:
    return _tagToText(boardName, 'sup', 0, {content, meta, options, pure});
  case MarkupNodeType.Text:
    return content;
  case MarkupNodeType.Tooltip: {
    const c = content.map(_nodeToText(boardName, pure, options)).join('');

    if (pure) {
      return c;
    }

    if (meta.flavour === MarkupFlavour.Markdown) {
      return `${c}???"${data.tooltip}"`;
    }

    return `[tooltip value="${data.tooltip}"]${c}[/tooltip]`;
  }

  case MarkupNodeType.Underlined:
    return _tagToText(boardName, 'u', 3, {content, meta, options, pure});
  case MarkupNodeType.UnorderedList: {
    const c = content.map(_nodeToText(boardName, pure, options, {data, type})).join('');

    if (pure) {
      return c;
    }

    return `[ul type="${data.type}"]\n${c}[/ul]`;
  }

  default:
    return '';
  }
};

function _tagToText(boardName, bbTag, markdown, {content, meta, options, pure}) {
  const nested = (typeof content === 'string') ? content : content.map(_nodeToText(boardName, pure, options)).join('');

  if (pure) {
    return nested;
  }

  if (meta.flavour === MarkupFlavour.BBCode) {
    return `[${bbTag}]${nested}[/${bbTag}]`;
  }

  const [t1, t2] = Array.isArray(markdown) ? markdown : (
    (typeof markdown === 'string') ? [markdown, markdown] : (() => {
      const [s, count] = MARKDOWN_VARIANT_MAP.get(meta.variant);

      const t = s.repeat(count || markdown);

      return [t, t];
    })()
  );

  return `${t1}${nested}${t2}`;
}

function _mapHtmlNode({attrs, children, content, type}) {
  if (type === 'text') {
    return {
      content: unescapeHtml(content),
      type: 'text'
    };
  }

  if (attrs['data-type'] === 'line-break') {
    return {
      content: null,
      type: 'line-break'
    };
  }

  const data = Object.entries(attrs)
    .filter(([key]) => key.startsWith('data-data-'))
    .reduce((acc, [key, value]) => {
      acc[_mapDataKey(key)] = _mapDataValue(attrs['data-type'], key, value);

      return acc;
    }, {});

  const node = {
    content: NODE_TYPES_NO_CONTENT.has(attrs['data-type']) ? null : children.map(_mapHtmlNode),
    meta: {
      flavour: attrs['data-meta-flavour']
    },
    type: attrs['data-type']
  };

  if (attrs['data-meta-variant']) {
    node.meta.variant = attrs['data-meta-variant'];
  }

  if (NODE_TYPES_TEXT_CONTENT.has(attrs['data-type'])) {
    node.content = data.text;

    delete data.text;
  }

  if (data && (Object.keys(data).length > 0)) {
    node.data = data;
  }

  return node;
}

function _collapseLineBreaks(nodes) {
  const list = [];

  let count = 0;

  for (const node of nodes) {
    if ((node.type === MarkupNodeType.LineBreak) && !node.data?.count) {
      ++count;
    } else {
      if (count > 0) {
        list.push({
          content: null,
          data: {count},
          type: MarkupNodeType.LineBreak
        });
      }

      list.push({
        ...node,
        content: Array.isArray(node.content) ? _collapseLineBreaks(node.content) : node.content
      });

      count = 0;
    }
  }

  if (count > 0) {
    list.push({
      content: null,
      data: {count},
      type: MarkupNodeType.LineBreak
    });
  }

  return list;
}

function _processNodes(nodes) {
  const list = [];

  for (const node of nodes) {
    if ([MarkupNodeType.OrderedList, MarkupNodeType.UnorderedList].includes(node.type)) {
      list.push({
        ...node,
        content: node.content.filter(({type}) => type === MarkupNodeType.ListItem).map(n => ({
          ...n,
          content: Array.isArray(n.content) ? _processNodes(n.content) : n.content
        }))
      });
    } else if (Array.isArray(node.content)) {
      list.push({
        ...node,
        content: _processNodes(node.content)
      });
    } else {
      list.push(node);
    }
  }

  return _collapseLineBreaks(list);
}

export function astToText(boardName, root, pure = false, {commandOutput = false, symbols = false} = {}) {
  if (!root) {
    return '';
  }

  return root.content
    .map(_nodeToText(boardName, pure, {commandOutput, symbols}))
    .join('');
}

export function textToAst(boardName, boardNames, text) {
  if (!text || typeof text !== 'string') {
    return null;
  }

  text = text.replace(/\r*\n|\r/gu, '\n');

  const context = new ProcessingContext({boardName, boardNames, text});

  for (const element of ELEMENTS) {
    if (typeof element.process === 'function') {
      element.process(context);
    } else if (typeof element.convert === 'function') {
      _process({
        context,
        check: element.check,
        conversionFunction: element.convert,
        escapable: Boolean(element.escapable),
        nestable: Boolean(element.nestable),
        rxCl: element.cl,
        rxOp: element.op
      });
    }
  }

  const htmlTree = HtmlParseStringify.parse(context.toHTML());

  return {
    content: _processNodes(htmlTree.map(_mapHtmlNode)),
    type: MARKUP_NODE_TYPE_ROOT
  };
}

export {
  MarkupBBCodeVariant,
  MarkupFlavour,
  MarkupLatexType,
  MarkupMarkdownVariant,
  MarkupNodeType,
  MarkupUnorderedListType,
  MARKUP_NODE_TYPE_ROOT,
  SUPPORTED_CODE_LANGS,
  getListBullet
};
